import { Spinner } from "@visiblethread/react-components";
import VirtualizedItems from "components/VirtualizedItems";
import { func } from "prop-types";
import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Document } from "react-pdf";
import { useDispatch, useSelector } from "react-redux";
import {
  setCurrentPage,
  setTotalPages,
} from "slices/documentViewer/documentViewerSlice";
import {
  selectDocumentZoom,
  selectTotalPages,
} from "slices/documentViewer/selectors";

import { A4_HEIGHT_72DPI, A4_WIDTH_72DPI, FILE_PROP_TYPE } from "./constants";
import { useSetZoomByContainer } from "./hooks";
import classes from "./PdfDocument.module.scss";
import PdfPage from "./PdfPage";
import { getCommonPageVars } from "./utils/styles";

const PdfDocument = forwardRef(({ file, onLoadSuccess }, forwardedRef) => {
  const totalPages = useSelector(selectTotalPages);
  const zoom = useSelector(selectDocumentZoom);
  const dispatch = useDispatch();

  const containerRef = useRef(null);
  const virtualizerRef = useRef();
  const [pdfKey, setPdfKey] = useState(0);

  useSetZoomByContainer(containerRef);

  // Address "sendWithPromise" error; see
  // https://github.com/wojtekmaj/react-pdf/issues/974#issuecomment-3293069045
  useLayoutEffect(() => {
    setPdfKey((prevKey) => prevKey + 1);
  }, [file]);

  const scrollToCenter = useCallback(async (index) => {
    const virtualizer = virtualizerRef?.current;

    dispatch(setCurrentPage(Math.max(1, index + 1)));
    virtualizer.scrollToRowByIndex(index, {
      align: "center",
      behavior: "smooth",
    });
  }, []);

  const style = getCommonPageVars(zoom);

  const onDocumentLoadSuccess = async (proxy) => {
    const { numPages } = proxy;

    const pageIntros = [];
    let baseWidth = A4_WIDTH_72DPI;

    // Extract the "intros" used in the page navigation,
    // this COULD be a taxing calc to loop through all pages
    // so _just_ extract the first {PAGE_INTRO_LIMIT} characters
    // of any given page.
    for (let i = 1; i <= numPages; i++) {
      const page = await proxy.getPage(i);
      const viewport = page.getViewport({ scale: 1 });
      const width = viewport.width;
      baseWidth = Math.max(baseWidth, width);

      const textContent = await page.getTextContent();
      const textChunks = textContent?.items || [];
      let intro = "";

      for (const item of textChunks) {
        intro += item.str + " ";
      }
      pageIntros.push(intro);
    }

    onLoadSuccess({
      baseWidth,
      totalPages: numPages,
      pageIntros,
    });
    dispatch(setTotalPages(numPages));
  };

  useImperativeHandle(forwardedRef, () => {
    return {
      virtualizer: virtualizerRef.current,
      scrollToCenter,
    };
  });

  return (
    <div className={classes["pdf_document"]} ref={containerRef} style={style}>
      <Document
        className={classes["pdf_document__react_pdf"]}
        file={file}
        key={pdfKey}
        loading={<Spinner size={null} />}
        onLoadSuccess={onDocumentLoadSuccess}
      >
        <VirtualizedItems
          data={Array.from({ length: totalPages })}
          estimateSize={() => A4_HEIGHT_72DPI}
          virtualizerRef={virtualizerRef}
        >
          {(_, virtualItem) => (
            <PdfPage
              index={virtualItem.index}
              key={virtualItem.key}
              ref={virtualizerRef.current?.measureElement}
            />
          )}
        </VirtualizedItems>
      </Document>
    </div>
  );
});

PdfDocument.displayName = "PdfDocument";

PdfDocument.propTypes = {
  file: FILE_PROP_TYPE,
  onLoadSuccess: func,
};

export default memo(PdfDocument, (prev, next) => {
  const prevFile = prev.file;
  const nextFile = next.file;

  return Object.is(prevFile, nextFile);
});
